/* eslint-disable no-unused-expressions */
/* eslint-disable consistent-return */
import api, { isResponseHandled } from '../api';
import app from '../app';
import { throttle, THROTTLE_TIME_INTERVAL, THROTTLE_RETRY_ATTEMPTS } from '../helpers';
import userService from '../user/user-service';
import { getOauthReturnUrl } from '../utils';

import {
    BASKET_STORAGE_KEY,
    BALLOT_BASKET_STORAGE_KEY,
    BASKET_DATE_CREATED_KEY,
    BALLOT_BASKET_DATE_CREATED_KEY,
    BASKET_TTL_MS,
    CHECKOUT_URL_KEY,
    DEFAULT_BASKET
} from './constants';

class BasketService {
    constructor() {
        this.onBasketUpdateSubscribers = [];
        this.onBallotBasketUpdateSubscribers = [];
        this._basket = null;
        this._ballotbasket = null;
        this._dateCreated = null;
        this._ballotBasketDateCreated = null;
    }

    getBasketDateCreated(isConfiguredForBallot) {
        if (isConfiguredForBallot) {
            if (this._ballotBasketDateCreated === null) {
                const ballotBasketDateCreated = localStorage.getItem(BALLOT_BASKET_DATE_CREATED_KEY);
                if (ballotBasketDateCreated) {
                    this._ballotBasketDateCreated = parseInt(ballotBasketDateCreated, 10);
                }
            }
            return this._ballotBasketDateCreated;
        }
        if (this._dateCreated === null) {
            const dateCreated = localStorage.getItem(BASKET_DATE_CREATED_KEY);
            if (dateCreated) {
                this._dateCreated = parseInt(dateCreated, 10);
            }
        }
        return this._dateCreated;
    }

    updateBasketDateCreated(isConfiguredForBallot) {
        isConfiguredForBallot ? localStorage.setItem(BALLOT_BASKET_DATE_CREATED_KEY, Date.now()) :
            localStorage.setItem(BASKET_DATE_CREATED_KEY, Date.now());
    }

    getBasket(isConfiguredForBallot) {
        if (isConfiguredForBallot) {
            if (this._ballotbasket === null) {
                const storedProductGroups = JSON.parse(localStorage.getItem(BALLOT_BASKET_STORAGE_KEY));
                const ballotBasketDateCreated = this.getBasketDateCreated(isConfiguredForBallot);

                if (storedProductGroups) this.setBasket({ productGroups: storedProductGroups }, isConfiguredForBallot);
                else this._ballotbasket = DEFAULT_BASKET;
                if (ballotBasketDateCreated && this.isBasketExpired(ballotBasketDateCreated)) this.cleanBasket(isConfiguredForBallot);
            }

            return this._ballotbasket;
        }
        if (this._basket === null) {
            const storedProductGroups = JSON.parse(localStorage.getItem(BASKET_STORAGE_KEY));
            const dateCreated = this.getBasketDateCreated(isConfiguredForBallot);

            if (storedProductGroups) this.setBasket({ productGroups: storedProductGroups }, isConfiguredForBallot);
            else this._basket = DEFAULT_BASKET;
            if (dateCreated && this.isBasketExpired(dateCreated)) { this.cleanBasket(isConfiguredForBallot); }
        }

        return this._basket;
    }

    setBasket(basket, isConfiguredForBallot) {
        if (isConfiguredForBallot) {
            this._ballotbasket = basket;
            this._ballotbasket.isEmpty = this._ballotbasket ? Object.keys(basket.productGroups).length === 0 :
                false;
        } else {
            this._basket = basket;
            this._basket.isEmpty = this._basket ? Object.keys(basket.productGroups).length === 0 :
                false;
        }
    }

    isBasketExpired(dateCreated) {
        return Date.now() > (dateCreated + BASKET_TTL_MS);
    }

    saveProductGroups(productGroups, isConfiguredForBallot) {
        if (localStorage) {
            const dateCreated = this.getBasketDateCreated(isConfiguredForBallot);

            if (!dateCreated || this.isBasketExpired(dateCreated)) this.updateBasketDateCreated(isConfiguredForBallot);

            (isConfiguredForBallot) ? localStorage.setItem(BALLOT_BASKET_STORAGE_KEY, JSON.stringify(productGroups)) :
                localStorage.setItem(BASKET_STORAGE_KEY, JSON.stringify(productGroups));

            this.setBasket({ productGroups }, isConfiguredForBallot);
            this.triggerOnBasketUpdate(isConfiguredForBallot);
        }
    }

    getProductGroups(isConfiguredForBallot) {
        return this.getBasket(isConfiguredForBallot).productGroups;
    }

    checkout(productGroups, mc) {
        const products = Object.keys(productGroups).reduce((result, groupId) => {
            const productGroup = productGroups[groupId];

            if (productGroup.products && productGroup.products.length > 0) {
                const preparedProducts = productGroup.products.map((product) => ({
                    type: product.type,
                    productId: product.productId,
                    quantity: product.quantity,
                    passId: product.passId,
                    audienceSubcategoryId: mc && product.audienceSubcategoryIdMc ?
                        product.audienceSubcategoryIdMc :
                        product.audienceSubcategoryId,
                }));

                result = [...result, ...preparedProducts];
            }

            return result;
        }, []);

        return this.throttleCheckout(products);
    }

    throttleCheckout(products) {
        return new Promise(async (resolve, reject) => {
            const token = await userService.getUserAccessToken();

            throttle(() => api.post(app.getRequiredConfig(CHECKOUT_URL_KEY), {
                redirectUri: getOauthReturnUrl({ noUrlAnchor: false }),
                recievedBasket: products
            }, {
                headers: {
                    'content-type': 'application/json',
                    authorization: `Bearer ${token}`
                }
            }), THROTTLE_TIME_INTERVAL, THROTTLE_RETRY_ATTEMPTS)
                .then((response) => {
                    if (!isResponseHandled(response)) {
                        return reject(new Error('unknown result from async data'));
                    }

                    resolve();
                }, (error) => {
                    if (error && error.code && error.code === 429) {
                        return reject(new Error('429'));
                    }
                    return reject();
                });
        });
    }

    onBasketUpdate(callback) {
        callback(this.getBasket(false));
        this.onBasketUpdateSubscribers.push(callback);

        return () => {
            this.onBasketUpdateSubscribers = this.onBasketUpdateSubscribers.filter((item) =>
                item !== callback);
        };
    }

    onBallotBasketUpdate(callback) {
        callback(this.getBasket(true));
        this.onBallotBasketUpdateSubscribers.push(callback);

        return () => {
            this.onBallotBasketUpdateSubscribers = this.onBallotBasketUpdateSubscribers.filter((item) =>
                item !== callback);
        };
    }

    triggerOnBasketUpdate(isConfiguredForBallot) {
        isConfiguredForBallot ? this.onBallotBasketUpdateSubscribers.forEach((cb) => cb(this.getBasket(isConfiguredForBallot))) :
            this.onBasketUpdateSubscribers.forEach((cb) => cb(this.getBasket(isConfiguredForBallot)));
    }

    cleanBasket(isConfiguredForBallot) {
        if (isConfiguredForBallot) {
            this._ballotbasket = DEFAULT_BASKET;
            if (localStorage) {
                localStorage.removeItem(BALLOT_BASKET_STORAGE_KEY);
                localStorage.removeItem(BALLOT_BASKET_DATE_CREATED_KEY);
            }
        } else {
            this._basket = DEFAULT_BASKET;
            if (localStorage) {
                localStorage.removeItem(BASKET_STORAGE_KEY);
                localStorage.removeItem(BASKET_DATE_CREATED_KEY);
            }
        }

        this.triggerOnBasketUpdate(isConfiguredForBallot);
    }
}

export default new BasketService();
